import { cva, type VariantProps } from 'class-variance-authority';
import { AlertTriangle, CheckCircle2, Info, MinusCircle, XCircle } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../lib/cn.js';

export const badgeVariants = cva(
  'inline-flex max-w-full items-center gap-1 whitespace-nowrap rounded-pill border px-2.5 py-0.5 text-helper font-medium [&_svg]:size-3.5 [&_svg]:shrink-0',
  {
    variants: {
      tone: {
        neutral: 'border-border-subtle bg-surface-subtle text-text-secondary',
        brand: 'border-brand-tint bg-brand-tint text-brand-hover',
        success: 'border-success/25 bg-success-bg text-success',
        warning: 'border-warning/25 bg-warning-bg text-warning',
        danger: 'border-danger/25 bg-danger-bg text-danger',
        info: 'border-info/25 bg-info-bg text-info',
        outline: 'border-border-control bg-surface-default text-text-secondary',
      },
    },
    defaultVariants: { tone: 'neutral' },
  },
);

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeVariants> {
  icon?: React.ReactNode;
}

export function Badge({ className, tone, icon, children, ...props }: BadgeProps) {
  return (
    <span className={cn(badgeVariants({ tone }), className)} {...props}>
      {icon}
      <span className="truncate">{children}</span>
    </span>
  );
}

export type StatusTone = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

const STATUS_ICON: Record<StatusTone, React.ReactNode> = {
  success: <CheckCircle2 aria-hidden />,
  warning: <AlertTriangle aria-hidden />,
  danger: <XCircle aria-hidden />,
  info: <Info aria-hidden />,
  neutral: <MinusCircle aria-hidden />,
};

export interface StatusBadgeProps extends Omit<BadgeProps, 'tone' | 'icon'> {
  status: StatusTone;
  /** Hide the icon only when the context already provides a non-color cue. */
  hideIcon?: boolean;
}

/** Semantic status: always pairs color with an icon and text (never color-only). */
export function StatusBadge({ status, hideIcon, ...props }: StatusBadgeProps) {
  return <Badge tone={status} icon={hideIcon ? undefined : STATUS_ICON[status]} {...props} />;
}
